import { activeResourceAtom } from "@app/resources/atom";
import { ResourceAssessment } from "@app/resources/components/ResourceAssessment";
import { ResourceRemainingViews } from "@app/resources/components/ResourceRemainingViews";
import { ResourcesListItem } from "@app/resources/components/ResourcesListItem";
import { ResourceUrl } from "@app/resources/components/ResourceUrl";
import { ResourceVideo } from "@app/resources/components/ResourceVideo";
import { ResourceYoutubeVideo } from "@app/resources/components/ResourceYoutubeVideo";
import { useResourceItem } from "@app/resources/hooks";
import {
  getViewLimitReachedTitle,
  isViewLimitReached,
} from "@app/resources/utils/resource-view-limit";
import { Resource, ResourceType } from "@core/shared/types";
import { trans } from "@mongez/localization";
import { LockIcon } from "lucide-react";

export function ActiveResource() {
  const activeResource = activeResourceAtom.useValue();

  const { allLinkedResources } = useResourceItem(activeResource as Resource);

  if (!activeResource) return null;

  const { url, type, title } = activeResource;

  return (
    <div className="py-20" id="active-resource">
      <h2 className="mb-8 text-2xl font-medium">{title}</h2>
      {activeResource.isLocked ? (
        <div className="space-y-3">
          <div className="py-2 text-center sm:py-3">
            <LockIcon className="mx-auto mb-2 h-16 w-16 text-gray-400 dark:text-gray-500 sm:h-8 sm:w-8" />
            <h4 className="mb-1 text-3xl font-semibold text-gray-900 dark:text-gray-100">
              {isViewLimitReached(activeResource)
                ? getViewLimitReachedTitle(activeResource)
                : trans("grades.resourceLocked")}
            </h4>
            {isViewLimitReached(activeResource) ? (
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {trans("grades.viewLimitReachedHint")}
              </p>
            ) : (
              activeResource.lockReason && (
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  {trans("grades." + activeResource.lockReason)}
                </p>
              )
            )}
          </div>
          {allLinkedResources.length > 0 && (
            <div className="space-y-2">
              {allLinkedResources.map((linkedResource, index) => (
                <div
                  key={linkedResource.id || index}
                  className="rounded border border-orange-200 bg-orange-50 p-2 dark:border-orange-700 dark:bg-orange-900/20 sm:p-3"
                >
                  <ResourcesListItem
                    resource={linkedResource}
                    className="!space-y-0"
                    isLinkedResource={true}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {type === ResourceType.Pdf && (
            <iframe
              src={url}
              className="h-screen w-full rounded-lg border-0 shadow-sm"
              title={title || "PDF Document"}
              // onLoad={trackResourceView}
            />
          )}
          {type === ResourceType.Image && (
            <div className="flex justify-center">
              <img
                src={url}
                alt={title || "Image"}
                className="h-[40rem] w-full rounded-lg object-contain shadow-sm"
              />
            </div>
          )}
          {type === ResourceType.Video && (
            <>
              <ResourceVideo resource={activeResource} />
              <ResourceRemainingViews
                resource={activeResource}
                className="mt-3"
              />
            </>
          )}
          {type === ResourceType.Youtube && (
            <ResourceYoutubeVideo resource={activeResource} />
          )}
          {type === ResourceType.Url && (
            <ResourceUrl resource={activeResource} />
          )}
          {type === ResourceType.Assessment && (
            <ResourceAssessment
              resource={activeResource}
              isLinkedResource={false}
            />
          )}
        </>
      )}
    </div>
  );
}
